import React, { useState } from "react";
import {
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Box,
  Button
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import dayjs from "dayjs";
import FeatureRequestTableHead from "./FeatureRequestTableHead";
import heartLoading from "../../../assests/heartLoading.gif";
import { useNavigate } from "react-router-dom";
import { Copy } from "lucide-react";

const PRIMARY_COLOR = "#bb1ccc";

export const statusPalette = {
  PENDING_REVIEW: {
    label: "Pending Review",
    bg: "rgba(250, 204, 21, 0.16)",
    color: "#a16207",
  },
  UNDER_REVIEW: {
    label: "Under Review",
    bg: "rgba(6, 182, 212, 0.16)",
    color: "#0f766e",
  },
  APPROVED: {
    label: "Approved",
    bg: "rgba(34, 197, 94, 0.16)",
    color: "#15803d",
  },
  IN_PROGRESS: {
    label: "In Progress",
    bg: "rgba(59, 130, 246, 0.16)",
    color: "#1d4ed8",
  },
  COMPLETED: {
    label: "Completed",
    bg: "rgba(34, 197, 94, 0.16)",
    color: "#15803d",
  },
  REJECTED: {
    label: "Rejected",
    bg: "rgba(248, 113, 113, 0.16)",
    color: "#b91c1c",
  },
};

export const categoryPalette = {
  NEW_FEATURE: { label: "New Feature", color: "#2563eb" },
  IMPROVEMENT: { label: "Enhancement", color: "#7c3aed" },
  BUG: { label: "Bug Fix", color: "#dc2626" },
  UI_UX: { label: "UI/UX", color: "#d946ef" },
  PERFORMANCE: { label: "Performance", color: "#ea580c" },
  OTHER: { label: "Other", color: "#6b7280" },
  SECURITY: { label: "Security", color: "#0ea5e9" },
  ONBOARD_PRODUCT: { label: "Onboard Product", color: "#059669" },
  REGISTER_PRODUCT: { label: "Register Product", color: "#059669" },
  EPIC: { label: "Epic", color: "#ea580c" },

};

const priorityPalette = {
  HIGH: {
    label: "High",
    color: "#b91c1c",
    background: "rgba(239,68,68,0.15)",
  },
  MEDIUM: {
    label: "Medium",
    color: "#92400e",
    background: "rgba(217,119,6,0.15)",
  },
  LOW: {
    label: "Low",
    color: "#1f2937",
    background: "rgba(107,114,128,0.2)",
  },
};

const CHIP_WIDTHS = {
  status: 120,
  category: 150,
  priority: 130,
  eta: 140,
};


const FeatureRequestList = ({
  requests,
  onSelect,
  onToggleUpvote,
  upvoting = false,
  sortBy = "",
  sortOrder = "asc",
  columnSearch = {},
  onSortChange,
  onSearchChange,
  loading
}) => {

  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(null);

  const handleCopyLink = async (event, request) => {
    // prevent row click
    if (event && typeof event.stopPropagation === "function") event.stopPropagation();

    try {
      const linkToCopy = `${window.location.origin}/feature-requests/${request.id}`;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(linkToCopy);
      } else {
        // fallback for older browsers
        const textarea = document.createElement("textarea");
        textarea.value = linkToCopy;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setCopiedId(request.id);
      setTimeout(() => setCopiedId(null), 2000);
      // optional: show console / toast
      console.log("Link copied:", linkToCopy);
    } catch (error) {
      console.error("Failed to copy link", error);
    }
  };

  return (
    <TableContainer
      component={Paper}
      sx={{ borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }}
    >
      <Table>
        <FeatureRequestTableHead
          sortBy={sortBy}
          sortOrder={sortOrder}
          columnSearch={columnSearch}
          onSortChange={onSortChange}
          onSearchChange={onSearchChange}
        />
       {loading ? (
  <TableBody>
    <TableRow>
      <TableCell colSpan={8} sx={{ p: 0 }}>
        <Box
          sx={{
            width: "100%",
            height: 400, // fixed height works better inside tables
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <img
            src={heartLoading}
            alt="Loading..."
 width={200}           />
          <Typography color="text.secondary" fontWeight={600}>
            Loading feature requests...
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  </TableBody>
) : !requests || requests.length === 0 ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={8} sx={{ p: 4 }}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  textAlign="center"
                  minHeight="40vh"
                  width="100%"
                >
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="text.primary"
                  >
                    No feature requests found
                  </Typography>
                  <Typography color="text.secondary">
                    Try adjusting your filters or submit a new request.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {requests.map((request) => {
              const statusConfig =
                statusPalette[request.status] || statusPalette.PENDING_REVIEW;
              const categoryConfig =
                categoryPalette[request.category] ||
                categoryPalette.NEW_FEATURE;
              const priorityConfig =
                priorityPalette[request.priority] || priorityPalette.MEDIUM;
              const submittedDateLabel = request.createdAt
                ? dayjs(request.createdAt).format("MMM D, YYYY")
                : "Not available";

              return (
                <TableRow
                  key={request.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => onSelect(request)}
                >
                  <TableCell
                    sx={{
                      width: "25%",
                      maxWidth: "25%",
                    }}
                  >
                    <Tooltip title={request.title} arrow
                      placement="top" >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          lineHeight: 1.4,
                          wordBreak: "break-word",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          width: "100%",

                        }}
                      >
                        {request.title}

                      </Typography>
                    </Tooltip>

                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusConfig.label === "Pending Review" ? "Pending" : statusConfig.label === "Under Review" ? "In Review" : statusConfig.label}
                      size="small"
                      sx={{
                        fontSize: "12px",
                        justifyContent: "center",
                        bgcolor: statusConfig.bg,
                        color: statusConfig.color,
                        border: "none",
                        padding: "5px 0", borderRadius: '6px'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={categoryConfig.label === "Onboard Product" ? "Onboarding" : categoryConfig.label === "Register Product" ? "Register" : categoryConfig.label}
                      size="small"
                      sx={{
                        fontSize: "12px",
                        backgroundColor: `${categoryConfig.color}15`,
                        color: categoryConfig.color,
                        justifyContent: "center",
                        border: "none",
                        padding: "5px 0", borderRadius: '6px'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={priorityConfig.label}
                      size="small"
                      sx={{
                        fontSize: "12px",
                        backgroundColor: priorityConfig.background,
                        color: priorityConfig.color,
                        justifyContent: "center",
                        border: "none",
                        padding: "5px 0", borderRadius: '6px'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Tooltip title="Upvote">
                        <IconButton
                          color={request.hasVoted ? "primary" : "default"}
                          onClick={(event) => {
                            event.stopPropagation();
                            onToggleUpvote(request);
                          }}
                          disabled={upvoting}
                          sx={{
                            bgcolor: request.hasVoted
                              ? "rgba(0,0,160,0.08)"
                              : "transparent",
                            "&:hover": {
                              bgcolor: request.hasVoted
                                ? "rgba(0,0,160,0.16)"
                                : "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          {upvoting ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: "#bb1ccc" }}
                            />
                          ) : (
                            <ThumbUpIcon
                              fontSize="small"
                              sx={{
                                color: request.hasVoted ? "#bb1ccc" : "inherit",
                              }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Typography sx={{ fontSize: "12px" }}>
                        {request.upvotes}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <ChatBubbleOutlineIcon
                        fontSize="small"
                        sx={{ color: "#6b7280" }}
                      />
                      <Typography sx={{ fontSize: "12px" }}>
                        {request.commentCount}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="left">
                    <Typography sx={{ fontSize: "12px" }}>
                      {request.submittedBy || "Anonymous"}
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography sx={{ fontSize: "12px" }}>
                      {submittedDateLabel}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">

                    <Tooltip title={copiedId === request.id ? "Copied" : "Copy link"} arrow placement="top"  >

                      <IconButton
                        variant="outlined"
                        size="small"
                        onClick={(e) => handleCopyLink(e, request)}
                        sx={{
                          fontSize: "12px",
                          textTransform: "none",
                          color: PRIMARY_COLOR,
                          borderRadius: 1,
                          whiteSpace: "nowrap",
                          display: "inline-flex",
                          gap: 1,

                        }}
                      >
                        <Copy size={14} />
                      </IconButton>
                    </Tooltip>

                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

export default FeatureRequestList;


